/**
 * Bazaarvoice Reviews and Q&A (V2), matched to the Yotpo treatment they
 * replace — measured on the US store, which still runs Yotpo.
 *
 * Reviews: the summary we had before — the overall rating large, stars and
 * "Based on N reviews" beside it, and a Write A Review button — rather than
 * V2's three columns (Rating Snapshot bars, Overall Rating, a row of empty
 * stars under "Review this Product"). Average Customer Ratings and the photo
 * carousel are dropped, as they were not part of the old block.
 *
 * Q&A: the old empty state — "Questions", "Have a question?", one line of
 * copy and a dark Ask a question button — rather than V2's pink button with
 * the copy underneath.
 *
 * V2 has no settings for any of this, and Bazaarvoice will not move us to
 * V1. But both apps render into an *open* shadow root on their container, so
 * the page can still reach them: a stylesheet is adopted by each root, and
 * the few text and button changes are made inside it. Bazaarvoice's class
 * names are hashed and can change with any of their releases, so nothing here
 * targets them. Each piece is found by what it is — the number, the review
 * count, a heading's text — and marked with a data-fb attribute; the CSS only
 * targets those marks and the few stable hooks Bazaarvoice does ship
 * (bv_rating_content1..3, #bv-question-btn). If a release changes the
 * structure, a mark is simply not set and that piece falls back to
 * Bazaarvoice's own styling — nothing breaks.
 *
 * Fonts: the old widget used "Founders Grotesk Light", which only Yotpo ever
 * loaded. "Founders Grotesk Text" is the light face on the page now and runs
 * about 14% narrower, so sizes here are set to match Yotpo's rendered widths
 * rather than its numbers: 54px reproduces the 48px score, 16px the 14px count.
 *
 * Both widgets re-render (filters, paging, re-measuring when a tab opens), so a
 * MutationObserver on each root reapplies the marks. Stylesheets are adopted
 * rather than appended, so a re-render cannot remove them.
 *
 * Write A Review calls BV.ui("rr", "submit_review"), Bazaarvoice's documented
 * entry to the same form its stars open. Until BV.ui exists the native stars
 * stay, so there is never a summary with no way to leave a review.
 */
(function () {
  var INK = "#3f2a2d";
  var LIGHT = "'Founders Grotesk Text','Founders Grotesk',sans-serif";
  var MEDIUM = "'Founders Grotesk Medium','Founders Grotesk Text',sans-serif";
  var PILL =
    "display:inline-flex!important;align-items:center;justify-content:center;min-height:37px;padding:0 22px!important;" +
    "border:0!important;border-radius:100px!important;background:" + INK + "!important;color:#fff!important;" +
    "font-family:" + MEDIUM + "!important;font-size:14px!important;font-weight:500!important;line-height:1!important;" +
    "letter-spacing:normal!important;text-transform:none!important;cursor:pointer;white-space:nowrap;box-shadow:none!important";

  var REVIEWS_CSS = [
    /* The tab already says Reviews. */
    "[data-fb=title]{display:none!important}",
    /* Rating Snapshot bars go; the Rating filter below still filters by stars. */
    ".bv_rating_content1{display:none!important}",
    /* Not part of the old summary. */
    "[data-fb=secondary],[data-fb=media]{display:none!important}",

    "[data-fb-row]{justify-content:center!important;align-items:center!important;flex-wrap:nowrap!important;gap:0;padding:8px 0 32px!important}",
    ".bv_rating_content2,.bv_rating_content3{flex:0 0 auto!important;width:auto!important;max-width:none!important;min-width:0!important}",

    /* Section labels ("Overall Rating", "Review this Product"). */
    ".bv_rating_content2 h3,.bv_rating_content3 h3{display:none!important}",
    "[data-fb=recommend]{display:none!important}",

    "[data-fb=overall]{display:flex!important;align-items:center;gap:12px}",
    "[data-fb=score]{font-family:" + LIGHT + "!important;font-size:54px!important;font-weight:300!important;line-height:1!important;color:" + INK + "!important;margin:0!important;padding-top:4px}",
    /* Stars above the count, 22px with a little air between them, as Yotpo had. */
    "[data-fb=count]{display:flex!important;flex-direction:column!important;align-items:flex-start!important;gap:7px;" +
      "font-family:" + LIGHT + "!important;font-size:16px!important;font-weight:300!important;line-height:1.1!important;color:" + INK + "!important;text-decoration:none!important}",
    "[data-fb=count] svg{width:22px!important;height:22px!important;margin-right:3px}",

    /* Divider and button, as on the current site. */
    ".bv_rating_content3[data-fb-ready]{border-left:1px solid rgba(63,42,45,.15);margin-left:36px!important;padding:12px 0 12px 36px!important}",
    ".bv_rating_content3[data-fb-ready]>:not([data-fb-write]){display:none!important}",
    "[data-fb-write]{" + PILL + "}",
    "[data-fb-write]:hover{background:#2a1c1e!important}",
    "[data-fb-write]:focus-visible{outline:2px solid " + INK + ";outline-offset:3px}",

    /* Review items, matched to Yotpo's: everything in ink and the light face,
       no Bazaarvoice blue. Bazaarvoice ships data-bv-v hooks on each part of a
       review, which are used here instead of its hashed class names. */
    /* :where() keeps this base rule at the lowest specificity, so the specific
       rules below (title in Medium, name size…) win over it. */
    "#reviews_container :where([data-bv-v=contentItem],[data-bv-v=contentItem] :not(svg,path,img)){color:" + INK + "!important;font-family:" + LIGHT + "!important}",
    /* Reviewer name: was blue bold 12px. Still a button (it opens the profile). */
    "#reviews_container button[aria-label^='See '][aria-label*=' profile'],#reviews_container button[aria-label^='See '][aria-label*=' profile'] *{font-size:17px!important;font-weight:300!important;text-decoration:none!important;background:none!important;padding:0!important;border:0!important}",
    "#reviews_container button[aria-label^='See '][aria-label*=' profile']:hover{text-decoration:underline!important}",
    "#reviews_container [data-bv-v=inlineProfile] :not(button)>span{font-size:13px!important}",
    "#reviews_container [data-bv-v=contentHeader] h3{font-family:" + MEDIUM + "!important;font-size:18px!important;font-weight:500!important;line-height:1.3!important}",
    "#reviews_container [data-fb=date]{color:#7a6d70!important;font-size:14px!important}",
    "#reviews_container [data-fb=body]{font-size:17px!important;line-height:1.5!important;font-weight:300!important}",
    "#reviews_container [data-bv-v=contentSummary] span{font-size:14px!important}",
    "#reviews_container [data-bv-v=feedback],#reviews_container [data-bv-v=feedback] *{font-size:14px!important}",
    "#reviews_container a{text-decoration:underline!important}",
    /* Pagination footer, in Yotpo's manner: no grey bar or boxed buttons — a
       centred row of plain ink chevrons around the count. Bazaarvoice only
       offers previous/next (no page numbers), so the count sits between them. */
    "[data-fb=pager]{background:transparent!important;border:0!important;padding:28px 0 8px!important;margin:0!important}",
    "[data-fb=pager]>div{display:flex!important;justify-content:center!important;align-items:center!important;gap:20px!important;background:transparent!important}",
    "[data-fb=pager] ul{display:contents!important}",
    "[data-fb=pager]>div>*,[data-fb=pager] li{margin:0!important;flex:0 0 auto!important;width:auto!important;position:static!important}",
    "[data-fb=pager] li{list-style:none;padding:0!important}",
    "[data-fb=pager] li:first-child{order:1}",
    "[data-fb=pager]>div>div{order:2;font-family:" + LIGHT + "!important;font-size:16px!important;font-weight:300!important;color:" + INK + "!important;height:auto!important}",
    "[data-fb=pager] li:last-child{order:3}",
    "[data-fb=pager] .prev,[data-fb=pager] .next{display:inline-flex!important;align-items:center;justify-content:center;width:38px!important;min-width:0!important;height:32px!important;padding:0!important;" +
      "background:transparent!important;border:0!important;border-radius:5px!important;box-shadow:none!important;color:" + INK + "!important;cursor:pointer}",
    /* Bazaarvoice's arrow is a square plus a chevron tinted blue through an
       SVG filter; ours is drawn in ink instead. */
    "[data-fb=pager] .prev>div,[data-fb=pager] .next>div{display:none!important}",
    "[data-fb=pager] .prev::before,[data-fb=pager] .next::before{content:'';width:8px;height:8px;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor}",
    "[data-fb=pager] .next::before{transform:rotate(-45deg);margin-right:3px}",
    "[data-fb=pager] .prev::before{transform:rotate(135deg);margin-left:3px}",
    "[data-fb=pager] .prev:hover,[data-fb=pager] .next:hover{background:rgba(63,42,45,.08)!important}",
    "[data-fb=pager] .prev:focus-visible,[data-fb=pager] .next:focus-visible{outline:2px solid " + INK + "!important;outline-offset:2px}",
    "[data-fb=pager] [disabled],[data-fb=pager] [aria-disabled=true]{color:#d8d8d8!important;cursor:default;background:transparent!important}",

    /* A hairline between reviews, as Yotpo had. */
    "#reviews_container>section{border-bottom:1px solid rgba(63,42,45,.14)!important;padding-bottom:24px!important;margin-bottom:24px!important}",
    "#reviews_container>section:last-of-type{border-bottom:0!important;margin-bottom:0!important}",
    /* Each review's stars at Yotpo's size. */
    "#reviews_container [data-bv-v=contentHeader] [role=img] svg{width:18px!important;height:18px!important;margin-right:2px}",

    /* "Filter Reviews" is a heading over an empty box when no filters are set
       up in Bazaarvoice. Marked only while the box is empty, so it comes back
       on its own if filters are switched on. */
    "[data-fb=nofilters]{display:none!important}",

    /* Sort, as Yotpo had it: no box, just "Sort by: Most Relevant" and a small
       chevron, right-aligned. The list keeps Bazaarvoice's keyboard handling
       (it is still their combobox); only its looks change. */
    "[data-fb=sort-box]{border:0!important;background:transparent!important;box-shadow:none!important;width:auto!important;min-width:0!important;margin-left:auto!important}",
    "[data-fb=sort-box]>div{padding:0!important;width:auto!important}",
    "[data-fb=sort-inner]{display:flex!important;align-items:center!important;gap:4px!important;padding:0!important;width:auto!important}",
    "[data-fb=sort-label]{display:block!important;width:auto!important;white-space:nowrap!important;padding:0!important;margin:0!important;line-height:1.3!important}",
    "[data-fb=sort-label],[data-fb=sort-label] *{font-family:" + LIGHT + "!important;font-size:14px!important;font-weight:300!important;color:" + INK + "!important}",
    "[data-fb=sort-label] span{display:inline!important;padding:0!important;margin:0!important}",
    "[data-fb=sort-label]::after{content:':'}",
    "[data-fb=sort-value],[data-fb=sort-value] *{font-family:" + MEDIUM + "!important;font-size:14px!important;font-weight:500!important;color:" + INK + "!important;width:auto!important;padding:0!important;line-height:1.3!important;white-space:nowrap!important}",
    "[data-fb=sort-value]:focus-visible{outline:2px solid " + INK + "!important;outline-offset:3px;border-radius:2px}",
    "[data-fb=sort-arrow]{display:flex!important;align-items:center}",
    "[data-fb=sort-arrow] svg{width:16px!important;height:16px!important}",
    /* Only the chevron stroke: the svg also holds two invisible spacer
       squares, which a blanket stroke would draw as a box. */
    "[data-fb=sort-arrow] svg path:last-child{stroke:" + INK + "!important}",
    /* The pop-up sits 8px under the text, right edges aligned. Bazaarvoice
       places it 41px down, measured for the box it no longer has. The 8px is
       padding, not offset: Bazaarvoice closes the list when the pointer leaves
       it, and a real gap closed it on the way from "Sort by" down to the list. */
    "[data-fb=sort-pop]{top:100%!important;padding-top:8px!important;background:transparent!important;right:0!important;left:auto!important;width:auto!important;z-index:20!important}",
    "[data-fb=sort-box],[data-fb=sort-box]>div,[data-fb=sort-inner]{min-height:0!important;height:auto!important}",
    /* "Relevancy Info" sits right under the sort text, where the open list
       lands; it steps aside while the list is open. */
    "[data-fb=sort-box]:has([aria-expanded=true])+[data-fb=sort-info]{visibility:hidden!important}",
    /* "Relevancy Info" only exists for "Most Relevant", so in the flow it
       grew and shrank the row with every sort change. Out of the flow, small,
       hanging under the sort text. */
    "[data-fb=sort-row]{position:relative!important}",
    "[data-fb=sort-info]{position:absolute!important;top:100%!important;right:0!important;margin-top:2px!important;white-space:nowrap!important;line-height:1!important}",
    "[data-fb=sort-info] button{padding:0!important;min-height:0!important;line-height:1.2!important}",
    "[data-fb=sort-info] button>span{display:inline-flex!important;align-items:center;gap:4px}",
    "[data-fb=sort-info],[data-fb=sort-info] *{font-family:" + LIGHT + "!important;font-size:12px!important;font-weight:300!important;color:" + INK + "!important}",
    "[data-fb=sort-info] svg{width:12px!important;height:12px!important}",
    "[data-fb=sort-list]{box-sizing:border-box!important;width:auto!important;min-width:180px!important;margin:0!important;padding:8px!important;border:1px solid #eaeaea!important;border-radius:4px!important;box-shadow:0 3px 10px rgba(0,0,0,.15)!important;background:#fff!important}",
    /* Rows fill the panel exactly: Bazaarvoice gives them a fixed width plus
       padding, which ran the highlight past the panel's edge. */
    "[data-fb=sort-list] [role=option]{box-sizing:border-box!important;width:100%!important;display:flex!important;align-items:center!important;min-height:36px!important;padding:0 10px!important;border-radius:4px!important;font-family:" + LIGHT + "!important;font-size:14px!important;font-weight:300!important;color:" + INK + "!important;background:transparent!important;cursor:pointer}",
    "[data-fb=sort-list] [role=option] *{font:inherit!important;color:inherit!important;background:transparent!important;border:0!important}",
    "[data-fb=sort-list] [role=option]>span{display:flex!important;align-items:center;gap:8px;width:auto!important;padding:0!important;position:static!important}",
    "[data-fb=sort-list] [role=option] svg path{fill:currentColor!important}",
    "[data-fb=sort-list] [role=option]:focus-visible{outline:2px solid " + INK + "!important;outline-offset:-2px}",
    "[data-fb=sort-list] [role=option]:hover{background:rgba(63,42,45,.07)!important}",
    "[data-fb=sort-list] [role=option][aria-selected=true]{background:" + INK + "!important;color:#fff!important;font-family:" + MEDIUM + "!important;font-weight:500!important}",

    /* Only a phone stacks; anything wider keeps rating and button side by side. */
    "@media (max-width:480px){" +
      "[data-fb-row]{flex-wrap:wrap!important}" +
      ".bv_rating_content2,.bv_rating_content3{flex:1 1 100%!important;display:flex;justify-content:center}" +
      ".bv_rating_content3[data-fb-ready]{border-left:0;margin:20px 0 0!important;padding:0!important}" +
    "}",
  ].join("");

  var QUESTIONS_CSS = [
    "[data-fb-qa]{display:flex!important;flex-direction:column;align-items:center;text-align:center}",
    /* The ask form and the question list open inside the same block; the
       heading stays first and they keep their own left-aligned layout. */
    "[data-fb-qa]>:not([data-fb]):not([data-fb-qa]){align-self:stretch;text-align:left}",
    "[data-fb=qa-title]{order:-1;font-family:" + MEDIUM + "!important;font-size:20px!important;font-weight:500!important;color:" + INK + "!important;margin:0 0 48px!important;text-transform:none!important;letter-spacing:normal!important}",
    "[data-fb=qa-empty]{order:2;display:block!important;font-family:" + LIGHT + "!important;font-size:17px!important;font-weight:300!important;color:" + INK + "!important;margin:0 0 20px!important}",
    "[data-fb=qa-empty] *{font-family:inherit!important;font-size:inherit!important;font-weight:inherit!important;color:inherit!important;justify-content:center!important}",
    "[data-fb=qa-empty]::before{content:'Have a question?';display:block;font-family:" + MEDIUM + ";font-size:20px;font-weight:500;margin-bottom:22px}",
    "[data-fb=qa-action]{order:3;margin:0!important}",
    "#bv-question-btn{" + PILL + ";min-width:212px}",
    "#bv-question-btn:hover{background:#2a1c1e!important}",
    "#bv-question-btn:focus-visible{outline:2px solid " + INK + ";outline-offset:3px}",
  ].join("");

  function adopt(root, css) {
    if (root.__fbStyled) return;
    root.__fbStyled = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
    } catch (e) {
      // Older browsers without constructable stylesheets.
      var style = document.createElement("style");
      style.setAttribute("data-fb-style", "");
      style.textContent = css;
      root.appendChild(style);
    }
  }

  function find(scope, test) {
    var all = scope.querySelectorAll("*");
    for (var i = 0; i < all.length; i++) {
      if (test(all[i])) return all[i];
    }
    return null;
  }

  function ownText(el) {
    var text = "";
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3) text += el.childNodes[i].textContent;
    }
    return text.trim();
  }

  function setMark(el, value) {
    if (el && el.getAttribute("data-fb") !== value) el.setAttribute("data-fb", value);
  }

  function headingText(el, text) {
    return /^H[1-6]$/.test(el.tagName) && el.textContent.trim() === text;
  }

  /* The block a heading introduces: climb until the next step up would also
     hold the summary row, so we never hide more than that block. */
  function blockOf(heading, keep) {
    var el = heading;
    while (el.parentElement && !el.parentElement.contains(keep)) el = el.parentElement;
    return el;
  }

  // ---- Reviews -------------------------------------------------------------

  var BADGE_LABELS = { "SkinGoal_1": "Skin Goal" };

  function markReviews(root) {
    var overall = root.querySelector(".bv_rating_content2");
    if (!overall) return;
    var row = overall.parentElement;
    if (row && !row.hasAttribute("data-fb-row")) row.setAttribute("data-fb-row", "");

    setMark(find(root, function (el) { return headingText(el, "Reviews") && el.tagName === "H2"; }), "title");

    var ratings = find(root, function (el) { return headingText(el, "Average Customer Ratings"); });
    if (ratings) setMark(blockOf(ratings, overall), "secondary");
    var media = find(root, function (el) { return headingText(el, "Customer Images and Videos"); });
    if (media) setMark(blockOf(media, overall), "media");

    var score = find(overall, function (el) {
      return el.children.length === 0 && /^\d(\.\d)?$/.test(el.textContent.trim());
    });
    setMark(score, "score");
    if (score) setMark(score.parentElement, "overall");

    // Review items: the relative date and the review text have no hook of
    // their own, so they are found by content.
    var items = root.querySelectorAll("#reviews_container [data-bv-v=contentItem]");
    for (var k = 0; k < items.length; k++) {
      var item = items[k];
      var dates = item.querySelectorAll("span");
      for (var d = 0; d < dates.length; d++) {
        if (dates[d].children.length === 0 && /^(a|an|\d+)\s+(second|minute|hour|day|week|month|year)s?\s+ago$/i.test(dates[d].textContent.trim())) {
          setMark(dates[d], "date");
        }
      }
      var summary = item.querySelector("[data-bv-v=contentSummary]");
      // Tag labels come straight from the Bazaarvoice field ids, and one of
      // them is the raw id "SkinGoal_1". Shown as the name it should have.
      var badges = item.querySelectorAll(".badge-label span");
      for (var b = 0; b < badges.length; b++) {
        var label = badges[b].textContent.trim();
        if (BADGE_LABELS[label] && badges[b].children.length === 0) badges[b].textContent = BADGE_LABELS[label];
      }
      if (summary && !summary.querySelector("[data-fb=body]")) {
        setMark(find(summary, function (el) {
          return el.tagName === "DIV" && ownText(el).length > 20;
        }), "body");
      }
    }

    // Filter heading: hidden only while Bazaarvoice has no filters to show.
    var filters = root.querySelector("#bv_review_filter_container");
    if (filters) {
      var hasFilters = false;
      for (var f = 0; f < filters.children.length; f++) {
        var part = filters.children[f];
        if (/^H\d$/.test(part.tagName)) continue;
        if (part.children.length || part.textContent.trim()) hasFilters = true;
      }
      if (!hasFilters) setMark(filters, "nofilters");
      else if (filters.getAttribute("data-fb") === "nofilters") filters.removeAttribute("data-fb");
    }

    // Sort control. Bazaarvoice's classes are hashed, so it is found from the
    // combobox (stable role + aria-controls) and its fixed nesting.
    var sort = root.querySelector("[role=combobox][aria-controls^='bv-reviews-sort']");
    if (sort) {
      setMark(sort, "sort-value");
      var inner = sort.parentElement;
      setMark(inner, "sort-inner");
      if (inner && inner.parentElement) setMark(inner.parentElement.parentElement, "sort-box");
      setMark(sort.previousElementSibling, "sort-label");
      setMark(sort.nextElementSibling, "sort-arrow");
      var listbox = root.querySelector("#" + sort.getAttribute("aria-controls"));
      setMark(listbox, "sort-list");
      if (listbox) setMark(listbox.parentElement, "sort-pop");
      var sortBox = inner && inner.parentElement && inner.parentElement.parentElement;
      if (sortBox && sortBox.nextElementSibling && /Relevancy/i.test(sortBox.nextElementSibling.textContent)) {
        setMark(sortBox.nextElementSibling, "sort-info");
        setMark(sortBox.parentElement, "sort-row");
      }
    }

    // The footer after the list (count + previous/next).
    var list = root.querySelector("#reviews_container");
    if (list && list.nextElementSibling) setMark(list.nextElementSibling, "pager");

    // "1437 Reviews" becomes "Based on 1437 reviews", as on the current site.
    var count = find(overall, function (el) {
      return /^\d[\d,.\s]*\s+Reviews?$/i.test(ownText(el));
    });
    if (count) {
      setMark(count, "count");
      for (var i = 0; i < count.childNodes.length; i++) {
        var node = count.childNodes[i];
        var m = node.nodeType === 3 && node.textContent.trim().match(/^(\d[\d,.\s]*)\s+Reviews?$/i);
        if (m) {
          var n = m[1].trim();
          node.textContent = "Based on " + n + (n === "1" ? " review" : " reviews");
        }
      }
    }

    setMark(find(overall, function (el) {
      return el.children.length === 0 && /recommend this product/i.test(el.textContent);
    }), "recommend");
  }

  function addWriteButton(root, productId) {
    var slot = root.querySelector(".bv_rating_content3");
    if (!slot || !window.BV || typeof window.BV.ui !== "function") return;
    if (!slot.querySelector("[data-fb-write]")) {
      var button = document.createElement("button");
      button.type = "button";
      button.setAttribute("data-fb-write", "");
      button.textContent = "Write A Review";
      button.addEventListener("click", function () {
        window.BV.ui("rr", "submit_review", { productId: productId });
      });
      slot.insertBefore(button, slot.firstChild);
    }
    if (!slot.hasAttribute("data-fb-ready")) slot.setAttribute("data-fb-ready", "");
  }

  function applyReviews(container) {
    var root = container.shadowRoot;
    if (!root || !root.querySelector(".bv_rating_content2")) return;
    adopt(root, REVIEWS_CSS);
    markReviews(root);
    addWriteButton(root, container.getAttribute("data-bv-product-id"));
  }

  // ---- Questions -----------------------------------------------------------

  var EMPTY_COPY = "Be the first to ask something about this product.";

  function applyQuestions(container) {
    var root = container.shadowRoot;
    if (!root) return;
    var title = find(root, function (el) { return headingText(el, "Questions"); });
    if (!title) return;
    adopt(root, QUESTIONS_CSS);

    setMark(title, "qa-title");
    var wrap = title.parentElement;
    if (wrap && !wrap.hasAttribute("data-fb-qa")) wrap.setAttribute("data-fb-qa", "");

    var button = root.getElementById ? root.getElementById("bv-question-btn") : root.querySelector("#bv-question-btn");
    if (button) {
      setMark(button.parentElement === wrap ? null : button.parentElement, "qa-action");
      if (button.textContent.trim() !== "Ask a question") {
        button.textContent = "Ask a question";
        button.setAttribute("aria-label", "Ask a question");
      }
    }

    // Empty state only: the visible line (not the screen-reader announcement,
    // which is a separate, visually hidden element and is left as it is).
    var empty = find(root, function (el) {
      return el.children.length === 0 && /^No questions have been asked/i.test(el.textContent.trim()) &&
        el.getAttribute("role") !== "alert" && !el.hasAttribute("aria-live");
    });
    if (!empty) empty = find(root, function (el) { return el.getAttribute("data-fb") === "qa-empty"; });
    if (empty) {
      var box = empty.parentElement && empty.parentElement !== wrap ? empty.parentElement : empty;
      setMark(box, "qa-empty");
      if (empty.textContent.trim() !== EMPTY_COPY) empty.textContent = EMPTY_COPY;
    }
  }

  // ---- Wiring --------------------------------------------------------------

  function watch(container, apply) {
    if (container.__fbWatched) return;
    container.__fbWatched = true;

    var pending = false;
    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        apply(container);
      });
    }

    // The shadow root appears when Bazaarvoice first renders; poll briefly
    // for it, then let the observer keep things right from there.
    var tries = 0;
    (function wait() {
      if (container.shadowRoot) {
        new MutationObserver(schedule).observe(container.shadowRoot, {
          childList: true,
          subtree: true,
          characterData: true,
        });
        schedule();
        return;
      }
      if (++tries < 120) setTimeout(wait, 250);
    })();
  }

  function init() {
    document.querySelectorAll('[data-bv-show="reviews"]').forEach(function (c) { watch(c, applyReviews); });
    document.querySelectorAll('[data-bv-show="questions"]').forEach(function (c) { watch(c, applyQuestions); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);
})();
